import RedoIconArrowLeft from "@redotech/redo-icons/arrow-left.svg";
import SearchSm from "@redotech/redo-icons/search-sm.svg";
import SettingsIcon from "@redotech/redo-icons/settings-02.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import classNames from "classnames";
import { memo, useContext, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CommandPaletteOpenContext } from "../../command-palette/contexts/command-palette-context";
import { Flex } from "../../flex";
import {
  SETTINGS_SEARCH_QUERY_PARAM,
  getSettingsSearchHref,
  normalizeSettingsSearchPhrase,
} from "../../settings-search/settings-search";
import { getSettingsSearchRank } from "../../settings-search/settings-search-rank";
import { Text } from "../../text";
import * as darkModeCss from "../../theme/component-colors-dark.module.css";
import { RedoButton } from "../buttons/redo-button";
import { RedoIcon } from "../icon/redo-icon";
import { RedoTextInput } from "../input/redo-text-input";
import { UserProfile } from "./common/redo-nav-user-profile";
import * as navigationCss from "./redo-navigation.module.css";
import {
  RedoSettingsNav as LegacyRedoSettingsNav,
  SettingNavItem,
  SettingsSectionEnum,
  getSearchHintTokens,
  getSettingItemSettingSearchLabels,
  getSettingSearchEvidence,
  settingsSectionToFriendly as legacySettingsSectionToFriendly,
  settingItemToProps,
  sortSettingsSections,
  type SettingItem,
  type SettingItemSearchTab,
  type SettingsSection,
} from "./redo-settings-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "./secondary/redo-secondary-nav-item";

export const settingsSectionToFriendly: Record<SettingsSectionEnum, string> = {
  ...legacySettingsSectionToFriendly,
  [SettingsSectionEnum.ORDER_FULFILLMENT]: "Order fulfillment",
};

export function filterSettingItemsBySearchEvidence<TItem extends SettingItem>({
  items,
  search,
}: {
  items: readonly TItem[];
  search: string;
}): TItem[] {
  const queryTokens = getSearchHintTokens(search);
  if (queryTokens.length === 0) {
    return [...items];
  }

  return items.filter((item) => {
    return !!getSettingSearchEvidence({
      pageLabel: item.friendlyName,
      search,
      pageValues: getSettingItemPageSearchValues(item),
      settingLabels: getSettingItemSettingSearchLabels(item),
      settingValues: getSettingItemSettingSearchValues(item),
    });
  });
}

export function prioritizeExactSettingSearchPhrase<
  TItem extends SearchableSettingItem,
>({ items, search }: { items: readonly TItem[]; search: string }): TItem[] {
  const normalizedSearch = normalizeSettingsSearchPhrase(search);
  if (!normalizedSearch) {
    return [...items];
  }

  return items
    .map((item, index) => ({
      exactPhraseMatch: getSettingItemSearchPhrases(item).some(
        (phrase) => normalizeSettingsSearchPhrase(phrase) === normalizedSearch,
      ),
      index,
      item,
    }))
    .sort(
      (a, b) =>
        Number(b.exactPhraseMatch) - Number(a.exactPhraseMatch) ||
        a.index - b.index,
    )
    .map(({ item }) => item);
}

function getSettingItemPageSearchValues(item: SettingItem): string[] {
  const sectionName =
    "sectionName" in item && typeof item.sectionName === "string"
      ? item.sectionName
      : undefined;

  return [item.friendlyName, sectionName].filter(
    (value): value is string => !!value,
  );
}

function getSettingItemSettingSearchValues(item: SettingItem): string[] {
  const matchValues = (item.searchMatches ?? []).flatMap((match) => [
    match.label,
    ...(match.searchTerms ?? []),
  ]);
  return [
    ...matchValues,
    ...(item.searchHints ?? []),
    ...(item.searchTerms ?? []),
  ].filter((value): value is string => !!value);
}

function getSettingItemSearchPhrases(item: SearchableSettingItem): string[] {
  return [
    item.friendlyName,
    item.sectionName,
    ...(item.searchTerms ?? []),
    ...(item.searchHints ?? []),
    ...(item.searchMatches ?? []).flatMap((match) => [
      match.label,
      ...(match.searchTerms ?? []),
    ]),
  ];
}

export const RedoSettingsNav = memo(function RedoSettingsNav(props: {
  sections: SettingsSection[];
  exitSettingsUrl?: string;
  hidden?: boolean;
  variant?: "legacy" | "redesigned";
}) {
  const { variant = "legacy", ...navProps } = props;
  return variant === "redesigned" ? (
    <NewRedoSettingsNav {...navProps} />
  ) : (
    <LegacyRedoSettingsNav {...navProps} />
  );
});

const NewRedoSettingsNav = memo(function NewRedoSettingsNav({
  sections,
  exitSettingsUrl,
  hidden = false,
}: {
  sections: SettingsSection[];
  exitSettingsUrl?: string;
  /**
   * When true, renders items invisibly for tree registration (global search).
   * Only renders when command palette is open.
   */
  hidden?: boolean;
}) {
  const isCommandPaletteOpen = useContext(CommandPaletteOpenContext);

  // Register "Settings" as parent node for breadcrumbs (Settings > General > Billing)
  const settingsNavItem = useMemo(
    () => ({ friendlyName: "Settings", Icon: SettingsIcon, url: undefined }),
    [],
  );
  const { id: settingsNodeId, TreeContext } = useNavTreeNode(settingsNavItem);

  const sortedSections = useMemo(
    () => sortSettingsSections(sections, settingsSectionToFriendly),
    [sections],
  );

  // When hidden, only render for tree registration when command palette is open
  if (hidden) {
    if (!isCommandPaletteOpen) {
      return null;
    }
    return (
      <TreeContext.Provider value={settingsNodeId}>
        {sortedSections.map((section) => (
          <SettingsSectionComponent
            hidden
            items={section.items}
            key={section.name}
            name={section.name}
          />
        ))}
      </TreeContext.Provider>
    );
  }

  if (exitSettingsUrl === undefined) {
    throw new Error("Visible settings navigation requires an exit URL");
  }

  return (
    <TreeContext.Provider value={settingsNodeId}>
      <VisibleSettingsNav
        exitSettingsUrl={exitSettingsUrl}
        sections={sortedSections}
      />
    </TreeContext.Provider>
  );
});

/**
 * The visible settings navigation with full UI (back button, search, user profile).
 */
export interface SearchableSettingItem extends SettingItem {
  sectionName: string;
}

export function flattenSettingsItemsForSearch(
  sections: readonly SettingsSection[],
): SearchableSettingItem[] {
  return sections.flatMap((section) =>
    flattenSettingItems(section.items, settingsSectionToFriendly[section.name]),
  );
}

function flattenSettingItems(
  items: readonly SettingItem[],
  sectionName: string,
): SearchableSettingItem[] {
  return items.flatMap((item) => [
    { ...item, sectionName },
    ...flattenSettingItems(item.children ?? [], sectionName),
  ]);
}

export interface SettingsSidebarSearchPage extends Pick<
  SettingItem,
  "UnfilledIcon" | "FilledIcon"
> {
  id: string;
  label: string;
  matches: SettingsSidebarSearchMatch[];
}

export interface SettingsSidebarSearchMatch {
  href: string;
  label: string;
  kind: "setting" | "group" | "page";
  /** The page tab holding the match; matches on one tab are contiguous. */
  tabLabel?: string;
}

export function getSettingsSidebarSearchPages({
  sections,
  search,
}: {
  sections: readonly SettingsSection[];
  search: string;
}): SettingsSidebarSearchPage[] {
  if (!search.trim()) {
    return [];
  }

  const pages = flattenSettingsItemsForSearch(sections).flatMap(
    (item): SettingsSidebarSearchPageResult[] => {
      const page = {
        id: item.id,
        label: item.friendlyName,
        UnfilledIcon: item.UnfilledIcon,
        FilledIcon: item.FilledIcon,
      };
      const matches = [
        ...(item.searchMatches ?? []).map((match) => ({
          ...match,
          kind: "setting" as const,
        })),
        ...(item.searchGroups ?? []).map((match) => ({
          ...match,
          kind: "group" as const,
        })),
      ]
        .map((match) => ({
          match,
          rank: getSettingsSearchRank({
            search,
            label: match.label,
            terms: match.searchTerms,
          }),
        }))
        .filter(
          (result): result is typeof result & { rank: number } =>
            result.rank !== undefined,
        )
        .sort((a, b) => {
          return (
            a.rank - b.rank ||
            Number(b.match.kind === "group") -
              Number(a.match.kind === "group") ||
            (a.match.settingId ?? a.match.href ?? a.match.label).localeCompare(
              b.match.settingId ?? b.match.href ?? b.match.label,
            )
          );
        })
        .filter(withoutSettingsAbsorbedByTheirGroup());
      const pageRank = getSettingsSearchRank({
        search,
        label: item.friendlyName,
        terms: [
          item.sectionName,
          ...(!item.searchMatches?.length && !item.searchGroups?.length
            ? (item.searchTerms ?? [])
            : []),
        ],
      });
      const bestMatchRank = matches[0]?.rank;
      if (
        pageRank !== undefined &&
        (bestMatchRank === undefined || pageRank <= bestMatchRank)
      ) {
        return [
          {
            nameMatchCount: 0,
            nameRank: pageRank <= MAX_NAME_RANK ? pageRank : undefined,
            rank: pageRank,
            page: {
              ...page,
              matches: [
                {
                  href: getSettingsSearchHref({ href: item.href, search: "" }),
                  label: item.friendlyName,
                  kind: "page",
                },
              ],
            },
          },
        ];
      }
      if (bestMatchRank !== undefined) {
        // Once the query names something on the page, rows it only describes
        // are noise next to it.
        const nameMatches = matches.filter(({ rank }) => rank <= MAX_NAME_RANK);
        const candidateMatches = nameMatches.length > 0 ? nameMatches : matches;
        const exactSettingMatches = candidateMatches.filter(
          ({ match, rank }) => match.kind === "setting" && rank === 1,
        );
        const visibleMatches =
          exactSettingMatches.length > 1 &&
          new Set(exactSettingMatches.map(({ match }) => match.label)).size ===
            1
            ? exactSettingMatches
            : candidateMatches;
        const labelCounts = new Map<string, number>();
        for (const { match } of visibleMatches) {
          const key = match.label.toLowerCase();
          labelCounts.set(key, (labelCounts.get(key) ?? 0) + 1);
        }
        const tabs = item.searchTabs ?? [];
        return [
          {
            nameMatchCount: visibleMatches.filter(
              ({ rank }) => rank <= MAX_NAME_RANK,
            ).length,
            nameRank: undefined,
            rank: bestMatchRank,
            page: {
              ...page,
              matches: groupMatchesByTab(visibleMatches, tabs).map(
                ({ match }) => {
                  const tabLabel = tabs.find(
                    (tab) => tab.id === match.tabId,
                  )?.label;
                  return {
                    href: getSettingsSearchHref({
                      href: match.href ?? item.href,
                      search: "",
                    }),
                    label:
                      (labelCounts.get(match.label.toLowerCase()) ?? 0) > 1 &&
                      match.contextLabel
                        ? `${match.label} · ${match.contextLabel}`
                        : match.label,
                    kind: match.kind,
                    ...(tabLabel !== undefined ? { tabLabel } : {}),
                  };
                },
              ),
            },
          },
        ];
      }
      return [];
    },
  );
  return pages.sort(compareSidebarSearchPages).map(({ page }) => page);
}

/** Ranks from getSettingsSearchRank that come from the page's or group's own
 *  name rather than its section, a description, or a typo. */
const MAX_NAME_RANK = 3;

interface SettingsSidebarSearchPageResult {
  /** Visible matches the query names, as opposed to ones it only describes. */
  nameMatchCount: number;
  nameRank: number | undefined;
  page: SettingsSidebarSearchPage;
  rank: number;
}

interface RankedSidebarSearchMatch {
  match: { groupId?: string; kind: "setting" | "group"; tabId?: string };
  rank: number;
}

// A group and the settings inside it usually match for the same words
// ("AI exchange agent" / "Enable AI exchange agent"), so a group the query
// names stands in for its settings unless one of them matches strictly better.
// A group that only matched through its description hides nothing, and neither
// does a setting with no group.
function withoutSettingsAbsorbedByTheirGroup(): (
  ranked: RankedSidebarSearchMatch,
) => boolean {
  const namedGroupRanks = new Map<string, number>();
  return (ranked) => {
    const { match, rank } = ranked;
    if (!match.groupId) {
      return true;
    }
    if (match.kind === "group") {
      if (rank <= MAX_NAME_RANK) {
        namedGroupRanks.set(match.groupId, rank);
      }
      return true;
    }
    const groupRank = namedGroupRanks.get(match.groupId);
    return groupRank === undefined || rank < groupRank;
  };
}

// Keeps each tab's matches together, tabs ordered by their best match and then
// by page order, so a result reads page > tab > setting.
function groupMatchesByTab<TMatch extends RankedSidebarSearchMatch>(
  matches: readonly TMatch[],
  tabs: readonly SettingItemSearchTab[],
): TMatch[] {
  if (tabs.length === 0) {
    return [...matches];
  }
  const pageOrder = new Map(tabs.map((tab, index) => [tab.id, index]));
  const tabKey = ({ match }: TMatch) =>
    match.tabId !== undefined && pageOrder.has(match.tabId) ? match.tabId : "";
  const bestRank = new Map<string, number>();
  for (const ranked of matches) {
    const key = tabKey(ranked);
    bestRank.set(key, Math.min(bestRank.get(key) ?? Infinity, ranked.rank));
  }
  const tabOrder = [...bestRank.keys()].sort(
    (a, b) =>
      (bestRank.get(a) ?? Infinity) - (bestRank.get(b) ?? Infinity) ||
      (pageOrder.get(a) ?? Infinity) - (pageOrder.get(b) ?? Infinity),
  );
  return [...matches].sort(
    (a, b) => tabOrder.indexOf(tabKey(a)) - tabOrder.indexOf(tabKey(b)),
  );
}

// A page the query names outright ("ret" -> Returns) comes before pages that
// only match through a setting inside them, however many of those there are.
// Among the latter, the page whose settings the query names most often is the
// one it is about: "exchange" is a Returns query before an Integrations one.
function compareSidebarSearchPages(
  a: SettingsSidebarSearchPageResult,
  b: SettingsSidebarSearchPageResult,
): number {
  if (a.nameRank !== undefined && b.nameRank !== undefined) {
    return (
      a.nameRank - b.nameRank ||
      a.page.label.length - b.page.label.length ||
      a.page.id.localeCompare(b.page.id)
    );
  }
  if (a.nameRank !== undefined || b.nameRank !== undefined) {
    return a.nameRank !== undefined ? -1 : 1;
  }
  return (
    a.rank - b.rank ||
    b.nameMatchCount - a.nameMatchCount ||
    a.page.id.localeCompare(b.page.id)
  );
}

export function filterSettingsSectionsByMatchingItemIds({
  matchingItemIds,
  sections,
}: {
  matchingItemIds: ReadonlySet<string>;
  sections: readonly SettingsSection[];
}): SettingsSection[] {
  return sections.flatMap((section) => {
    const items = filterSettingItemTree(section.items, matchingItemIds);
    return items.length > 0 ? [{ ...section, items }] : [];
  });
}

function filterSettingItemTree(
  items: readonly SettingItem[],
  matchingItemIds: ReadonlySet<string>,
): SettingItem[] {
  return items.flatMap((item) => {
    if (matchingItemIds.has(item.id)) {
      return [item];
    }

    const children = filterSettingItemTree(
      item.children ?? [],
      matchingItemIds,
    );
    return children.length > 0 ? [{ ...item, children }] : [];
  });
}

const VisibleSettingsNav = memo(function VisibleSettingsNav({
  sections,
  exitSettingsUrl,
}: {
  sections: SettingsSection[];
  exitSettingsUrl: string;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const locationSearch = new URLSearchParams(location.search).get(
    SETTINGS_SEARCH_QUERY_PARAM,
  );
  const [search, setSearch] = useState(locationSearch ?? "");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const hasSearch = search.trim().length > 0;
  const searchPages = useMemo(
    () => getSettingsSidebarSearchPages({ sections, search }),
    [sections, search],
  );
  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (locationSearch !== null) {
      navigate(
        getSettingsSearchHref({
          href: `${location.pathname}${location.search}${location.hash}`,
          search: "",
        }),
        { replace: true },
      );
    }
  };

  return (
    <Flex
      bgColor="primary"
      className={classNames(navigationCss.settingsNav, darkModeCss.darkMode)}
      data-arbiter="RedoSettingsNav"
      data-exclude-text-highlight="true"
      dir="column"
      grow={1}
      justify="space-between"
    >
      <Flex dir="column" gap="xl" overflow="hidden" py="3xl">
        <Flex px="sm">
          <Flex
            align="center"
            as="a"
            borderRadius="md"
            className={navigationCss.backButton}
            grow={1}
            linkProps={{ to: exitSettingsUrl }}
            px="3xl"
            py="sm"
          >
            <RedoIcon color="tertiary" Icon={RedoIconArrowLeft} size={16} />{" "}
            <Text fontSize="xs" fontWeight="regular" textColor="secondary">
              Exit settings
            </Text>
          </Flex>
        </Flex>
        <Flex dir="column" gap="xs" px="3xl">
          <RedoTextInput
            autoFocus
            IconLeading={SearchSm}
            placeholder="Search"
            ref={searchInputRef}
            setValue={handleSearchChange}
            trailingActions={
              search.length > 0 ? (
                <Flex pr="sm">
                  <RedoButton
                    aria-label="Clear search"
                    hierarchy="link"
                    IconLeading={XCloseIcon}
                    onClick={() => {
                      handleSearchChange("");
                      searchInputRef.current?.focus();
                    }}
                    size="xs"
                  />
                </Flex>
              ) : undefined
            }
            value={search}
          />
        </Flex>
        <Flex
          className={navigationCss.settingsNavItems}
          dir="column"
          gap="lg"
          px="xl"
        >
          {hasSearch ? (
            searchPages.length > 0 ? (
              searchPages.map((page) => (
                <Flex dir="column" gap="xs" key={page.id}>
                  {page.matches.some((match) => match.kind !== "page") && (
                    <Flex align="center" gap="md" px="md">
                      {page.UnfilledIcon && (
                        <RedoIcon
                          color="tertiary"
                          Icon={page.UnfilledIcon}
                          size={16}
                        />
                      )}
                      <Text
                        fontSize="xs"
                        fontWeight="regular"
                        textColor="tertiary"
                      >
                        {page.label}
                      </Text>
                    </Flex>
                  )}
                  {page.matches.map((match, index) => (
                    <Flex
                      dir="column"
                      gap="xs"
                      key={`${match.kind}:${match.label}:${match.href}`}
                      pl={
                        match.kind !== "page" && page.UnfilledIcon
                          ? "3xl"
                          : "none"
                      }
                    >
                      {match.tabLabel !== undefined &&
                        match.tabLabel !==
                          page.matches[index - 1]?.tabLabel && (
                          <Flex px="md">
                            <Text
                              fontSize="xs"
                              fontWeight="regular"
                              textColor="tertiary"
                            >
                              {match.tabLabel}
                            </Text>
                          </Flex>
                        )}
                      <Flex
                        dir="column"
                        pl={match.tabLabel !== undefined ? "xl" : "none"}
                      >
                        <RedoSecondaryNavItem
                          {...settingItemToProps({
                            href: match.href,
                            friendlyName: match.label,
                            id: `${page.id}:${match.kind}:${match.label}:${match.href}`,
                            UnfilledIcon:
                              match.kind === "page"
                                ? page.UnfilledIcon
                                : undefined,
                            FilledIcon:
                              match.kind === "page"
                                ? page.FilledIcon
                                : undefined,
                          })}
                          selected={
                            `${location.pathname}${location.search}` ===
                            match.href
                          }
                        />
                      </Flex>
                    </Flex>
                  ))}
                </Flex>
              ))
            ) : (
              <Text fontSize="xs" textColor="tertiary">
                No settings found
              </Text>
            )
          ) : (
            sections.map((section) => (
              <SettingsSectionComponent
                items={section.items}
                key={section.name}
                name={section.name}
              />
            ))
          )}
        </Flex>
      </Flex>
      <UserProfile collapsed={false} />
    </Flex>
  );
});

/**
 * Renders a settings section with its items.
 * When hidden, registers with tree context for global search with proper breadcrumbs.
 */
const SettingsSectionComponent = memo(function SettingsSectionComponent({
  name,
  items,
  hidden = false,
}: SettingsSection & { hidden?: boolean }) {
  // Register section as parent node for breadcrumbs (e.g., "General", "AI")
  const sectionNavItem = useMemo(
    () => ({
      friendlyName: settingsSectionToFriendly[name],
      Icon: undefined,
      url: undefined,
    }),
    [name],
  );
  const { id: sectionNodeId, TreeContext } = useNavTreeNode(sectionNavItem);

  if (hidden) {
    return (
      <TreeContext.Provider value={sectionNodeId}>
        {items.map((item) => (
          <SettingNavItem hidden item={item} key={item.id} />
        ))}
      </TreeContext.Provider>
    );
  }

  return (
    <TreeContext.Provider value={sectionNodeId}>
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="regular" textColor="secondary">
          {settingsSectionToFriendly[name]}
        </Text>
        <Flex dir="column" gap="xs">
          {items.map((item) => (
            <SettingNavItem item={item} key={item.id} />
          ))}
        </Flex>
      </Flex>
    </TreeContext.Provider>
  );
});
